import { apiRequest } from './api'

// Users API service
export const usersService = {
  // Get all users (admin only)
  getAll: async (params = {}) => {
    // Build query parameters - convert page/page_size to skip/limit
    const queryParams = {}

    if (params.page && params.page_size) {
      queryParams.skip = (params.page - 1) * params.page_size
      queryParams.limit = params.page_size
    } else if (params.page_size) {
      queryParams.limit = params.page_size
    }

    if (params.search) queryParams.search = params.search
    if (params.role) queryParams.role = params.role
    if (params.status) {
      // Convert frontend status filter to backend format
      queryParams.is_active = params.status === 'active' ? 'true' : params.status === 'inactive' ? 'false' : undefined
    }

    const response = await apiRequest.get('/users/', { params: queryParams })

    // Handle paginated response format
    if (response.data.users) {
      return {
        ...response.data,
        users: response.data.users.map(user => ({
          ...user,
          is_active: user.active,
          created_at: user.created_on,
          updated_at: user.changed_on,
          last_login_at: user.last_login
        }))
      }
    } else if (Array.isArray(response.data)) {
      // Simple array response (fallback)
      return {
        users: response.data.map(user => ({
          ...user,
          is_active: user.active,
          created_at: user.created_on,
          updated_at: user.changed_on,
          last_login_at: user.last_login
        })),
        total_count: response.data.length,
        total_pages: 1,
        page: 1,
        page_size: response.data.length,
        has_next: false,
        has_previous: false
      }
    } else {
      // Fallback
      return {
        users: [],
        total_count: 0,
        total_pages: 0,
        page: 1,
        page_size: 10,
        has_next: false,
        has_previous: false
      }
    }
  },

  // Get user by ID
  getById: async (id) => {
    const response = await apiRequest.get(`/users/${id}`)
    const user = response.data

    // Map backend field names to frontend expectations
    return {
      ...user,
      is_active: user.active,
      created_at: user.created_on,
      updated_at: user.changed_on,
      last_login_at: user.last_login
    }
  },

  // Create new user (admin only)
  create: async (userData) => {
    // Map frontend field names to backend expectations
    const backendData = {
      ...userData,
      active: userData.is_active,
    }
    delete backendData.is_active
    // Keep brand_access for backend processing

    const response = await apiRequest.post('/users/', backendData)
    const user = response.data

    return {
      ...user,
      is_active: user.active,
      created_at: user.created_on,
      updated_at: user.changed_on,
      last_login_at: user.last_login
    }
  },

  // Update user (admin only)
  update: async (id, userData) => {
    // Map frontend field names to backend expectations
    const backendData = {
      ...userData,
    }


    // Handle is_active to active mapping properly
    if (userData.hasOwnProperty('is_active')) {
      backendData.active = userData.is_active
      delete backendData.is_active
    }

    // Log the data being sent for debugging
    console.log('Updating user with data:', backendData)

    const response = await apiRequest.put(`/users/${id}`, backendData)
    const user = response.data

    return {
      ...user,
      is_active: user.active,
      created_at: user.created_on,
      updated_at: user.changed_on,
      last_login_at: user.last_login
    }
  },

  // Update user role (admin only)
  updateRole: async (id, roleData) => {
    const response = await apiRequest.put(`/users/${id}`, roleData)
    const user = response.data

    return {
      ...user,
      is_active: user.active,
      created_at: user.created_on,
      updated_at: user.changed_on,
      last_login_at: user.last_login
    }
  },

  // Delete user (soft delete, admin only)
  delete: async (id) => {
    const response = await apiRequest.delete(`/users/${id}`)
    return response.data
  },

  // Get current user profile
  getProfile: async () => {
    const response = await apiRequest.get('/auth/me')
    return response.data
  },

  // Update current user profile
  updateProfile: async (userData) => {
    const response = await apiRequest.put('/auth/profile', userData)
    return response.data
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await apiRequest.post('/auth/change-password', passwordData)
    return response.data
  },

  // Get user's brand access
  getBrandAccess: async (userId) => {
    const response = await apiRequest.get(`/users/${userId}/brand-access`)
    return response.data
  },

  // Update user's brand access (admin only)
  updateBrandAccess: async (userId, brandAccessData) => {
    const response = await apiRequest.put(`/users/${userId}/brand-access`, brandAccessData)
    return response.data
  },

  // Get user statistics
  getStats: async (userId) => {
    const response = await apiRequest.get(`/users/${userId}/stats`)
    return response.data
  },

  // Get users for dropdown/select
  getForSelect: async () => {
    const response = await apiRequest.get('/users/?size=1000&is_active=true')
    return response.data.map(user => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name} (${user.username})`,
      ...user
    }))
  },

  // Check area manager conflicts
  checkAreaConflicts: async (areaIds, userRole) => {
    const response = await apiRequest.post('/users/check-area-conflicts', {
      area_ids: areaIds,
      user_role: userRole
    })
    return response.data
  }
}
